import React, { useState, useEffect } from 'react';
import { Modal, Table, InputNumber, message, Tag, Space, Alert, Spin } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { getCreditableItems, createCreditNote } from '../../services/creditNoteService';
import './PartialCreditModal.css';

/**
 * PartialCreditModal - Modal for creating partial credit notes
 * Allows users to select specific items and quantities to credit from an invoice
 * 
 * @param {boolean} visible - Whether the modal is visible
 * @param {Object} invoice - The invoice object to create credit note for
 * @param {Function} onCancel - Callback when modal is cancelled
 * @param {Function} onSuccess - Callback when credit note is successfully created
 */
const PartialCreditModal = ({ visible, invoice, onCancel, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [creditableItems, setCreditableItems] = useState([]);
    const [invoiceInfo, setInvoiceInfo] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [quantities, setQuantities] = useState({});

    // Load creditable items when modal opens
    useEffect(() => {
        if (visible && invoice?.id) {
            loadCreditableItems();
        } else {
            // Reset state when modal closes
            setCreditableItems([]);
            setSelectedRowKeys([]);
            setQuantities({});
            setInvoiceInfo(null);
        }
    }, [visible, invoice]);

    /**
     * Load creditable items from backend
     */
    const loadCreditableItems = async () => {
        setLoading(true);
        try {
            const response = await getCreditableItems(invoice.id);
            
            if (response && response.success) {
                const data = response.data;
                setCreditableItems(data.items || []);
                setInvoiceInfo({
                    invoiceNumber: data.invoiceNumber,
                    cancellationStatus: data.cancellationStatus,
                    hasCreditNotes: data.hasCreditNotes,
                    totalAmount: data.totalAmount
                });
                
                // Initialize quantities with remaining quantities
                const initialQuantities = {};
                (data.items || []).forEach(item => {
                    if (item.canCredit) {
                        initialQuantities[item.id] = item.quantityRemaining;
                    }
                });
                setQuantities(initialQuantities);
            } else {
                message.error('Error al cargar items acreditables');
            }
        } catch (error) {
            console.error('Error loading creditable items:', error);
            message.error(error.message || 'Error al cargar items de la factura');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle quantity change for an item
     */
    const handleQuantityChange = (itemId, value) => {
        setQuantities(prev => ({
            ...prev,
            [itemId]: value
        }));
    };

    /**
     * Handle row selection change
     */
    const handleRowSelectionChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    /**
     * Calculate total amount to be credited
     */
    const calculateCreditTotal = () => {
        let total = 0;
        selectedRowKeys.forEach(itemId => {
            const item = creditableItems.find(i => i.id === itemId);
            if (item) {
                const quantity = quantities[itemId] || item.quantityRemaining;
                total += item.unitPrice * quantity;
            }
        });
        return total;
    };

    /**
     * Handle form submission
     */
    const handleSubmit = async () => {
        // Validate
        if (selectedRowKeys.length === 0) {
            message.warning('Por favor seleccione al menos un producto');
            return;
        }

        // Build items array for API
        const items = selectedRowKeys.map(itemId => {
            const item = creditableItems.find(i => i.id === itemId);
            const quantity = quantities[itemId] || item.quantityRemaining;
            
            return {
                invoiceItemId: parseInt(itemId),
                quantity: quantity
            };
        });

        // Confirm with user
        const creditTotal = calculateCreditTotal();
        const itemsCount = items.length;
        const totalItemsCount = creditableItems.length;
        
        Modal.confirm({
            title: '¿Crear Nota de Crédito Parcial?',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>Se creará una Nota de Crédito para:</p>
                    <p><strong>Factura:</strong> {invoiceInfo?.invoiceNumber}</p>
                    <p><strong>Items seleccionados:</strong> {itemsCount} de {totalItemsCount}</p>
                    <p><strong>Monto a acreditar:</strong> ${creditTotal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <br />
                    <p style={{ color: '#ff4d4f' }}>
                        <strong>Nota:</strong> Esta acción se registrará en AFIP. Los items no seleccionados podrán acreditarse posteriormente.
                    </p>
                </div>
            ),
            okText: 'Crear Nota de Crédito',
            okType: 'primary',
            cancelText: 'Cancelar',
            onOk: async () => {
                await executeCreateCreditNote(items);
            }
        });
    };

    /**
     * Execute credit note creation
     */
    const executeCreateCreditNote = async (items) => {
        setSubmitting(true);
        try {
            message.loading({ content: 'Creando Nota de Crédito...', key: 'partialCredit', duration: 0 });
            
            const response = await createCreditNote(invoice.id, {
                reason: 'Devolución parcial de productos',
                cancellationType: 'partial',
                items: items
            });

            if (response.success) {
                const creditNoteNumber = response.creditNote?.numero_comprobante || 'N/A';
                const stockRestoredCount = response.creditNote?.stockRestoredCount || 0;
                const stockMsg = stockRestoredCount > 0 ? ` Stock restaurado: ${stockRestoredCount} producto${stockRestoredCount > 1 ? 's' : ''}.` : '';
                message.success({
                    content: `Nota de Crédito ${creditNoteNumber} creada.${stockMsg}`,
                    key: 'partialCredit',
                    duration: 5
                });
                
                // Call success callback
                if (onSuccess) {
                    onSuccess(response);
                }
            } else {
                throw new Error(response.message || 'Error al crear nota de crédito');
            }
        } catch (error) {
            console.error('Error creating partial credit note:', error);
            message.error({
                content: error.message || 'Error al crear nota de crédito',
                key: 'partialCredit',
                duration: 5
            });
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Format price for display
     */
    const formatPrice = (price) => {
        if (typeof price !== 'number' || isNaN(price)) return 'N/A';
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    /**
     * Table columns
     */
    const columns = [
        {
            title: 'Producto',
            dataIndex: 'description',
            key: 'description',
            width: '40%',
            ellipsis: true,
        },
        {
            title: 'Precio Unit.',
            dataIndex: 'unitPrice',
            key: 'unitPrice',
            width: '15%',
            align: 'right',
            render: (price) => formatPrice(price),
        },
        {
            title: 'IVA',
            dataIndex: 'ivaRate',
            key: 'ivaRate',
            width: '10%',
            align: 'center',
            render: (rate) => <Tag color="blue">{rate}%</Tag>,
        },
        {
            title: 'Disponible',
            dataIndex: 'quantityRemaining',
            key: 'quantityRemaining',
            width: '15%',
            align: 'center',
            render: (qty, record) => (
                <Space>
                    <Tag color={record.quantityCredited > 0 ? 'orange' : 'green'}>
                        {qty}
                    </Tag>
                    {record.quantityCredited > 0 && (
                        <span style={{ fontSize: '12px', color: '#999' }}>
                            (Acreditado: {record.quantityCredited})
                        </span>
                    )}
                </Space>
            ),
        },
        {
            title: 'Cantidad a Acreditar',
            key: 'creditQuantity',
            width: '20%',
            align: 'center',
            render: (_, record) => (
                <InputNumber
                    min={1}
                    max={record.quantityRemaining}
                    value={quantities[record.id] || record.quantityRemaining}
                    onChange={(value) => handleQuantityChange(record.id, value)}
                    disabled={!selectedRowKeys.includes(record.id) || !record.canCredit}
                    style={{ width: '80px' }}
                />
            ),
        },
    ];

    /**
     * Row selection configuration
     */
    const rowSelection = {
        selectedRowKeys,
        onChange: handleRowSelectionChange,
        getCheckboxProps: (record) => ({
            disabled: !record.canCredit,
            name: record.description,
        }),
    };

    /**
     * Get cancellation status tag
     */
    const getCancellationStatusTag = () => {
        if (!invoiceInfo) return null;
        
        const statusConfig = {
            none: { color: 'default', text: 'Sin Acreditación' },
            partial: { color: 'warning', text: 'Parcialmente Acreditada' },
            full: { color: 'error', text: 'Completamente Acreditada' }
        };
        
        const config = statusConfig[invoiceInfo.cancellationStatus] || statusConfig.none;
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    return (
        <Modal
            title={
                <Space>
                    <span>Crear Nota de Crédito Parcial</span>
                    {invoiceInfo && getCancellationStatusTag()}
                </Space>
            }
            open={visible}
            onCancel={onCancel}
            onOk={handleSubmit}
            confirmLoading={submitting}
            width={1000}
            okText="Crear Nota de Crédito"
            cancelText="Cancelar"
            okButtonProps={{ disabled: selectedRowKeys.length === 0 }}
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                    <p style={{ marginTop: '16px' }}>Cargando items...</p>
                </div>
            ) : (
                <>
                    {/* Invoice Info */}
                    {invoiceInfo && (
                        <Alert
                            message="Información de la Factura"
                            description={
                                <div>
                                    <p><strong>Número:</strong> {invoiceInfo.invoiceNumber}</p>
                                    <p><strong>Monto Total:</strong> {formatPrice(invoiceInfo.totalAmount)}</p>
                                    {invoiceInfo.hasCreditNotes && (
                                        <p style={{ color: '#fa8c16' }}>
                                            <strong>Nota:</strong> Esta factura ya tiene notas de crédito previas.
                                        </p>
                                    )}
                                </div>
                            }
                            type="info"
                            showIcon
                            style={{ marginBottom: '16px' }}
                        />
                    )}

                    {/* Items Table */}
                    <Table
                        dataSource={creditableItems.filter(item => item.canCredit)}
                        columns={columns}
                        rowKey="id"
                        rowSelection={rowSelection}
                        pagination={false}
                        scroll={{ y: 400 }}
                        size="small"
                        locale={{
                            emptyText: 'No hay items disponibles para acreditar'
                        }}
                    />

                    {/* Summary */}
                    {selectedRowKeys.length > 0 && (
                        <div style={{ 
                            marginTop: '16px', 
                            padding: '12px', 
                            backgroundColor: '#f0f2f5',
                            borderRadius: '4px'
                        }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <strong>Items seleccionados:</strong>
                                    <span>{selectedRowKeys.length} de {creditableItems.length}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <strong>Monto a acreditar:</strong>
                                    <span style={{ fontSize: '16px', color: '#1890ff' }}>
                                        {formatPrice(calculateCreditTotal())}
                                    </span>
                                </div>
                            </Space>
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

export default PartialCreditModal;
